/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Search } from 'lucide-react'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'
import './Dropdown.css'

export interface DropdownOption {
  value: string
  label: string
  disabled?: boolean
  /** Optional icon shown in the trigger (when selected) and in the option list. */
  icon?: React.ReactNode
  /** Optional native tooltip shown on hover over the option (and the trigger when selected). */
  title?: string
}

export interface DropdownProps {
  options: DropdownOption[]
  value?: string
  placeholder?: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  required?: boolean
  title?: string
  portal?: boolean
  /** Applied to the root `.dropdown` wrapper (e.g. width: '100%' in flex layouts) */
  rootStyle?: React.CSSProperties
  /** Applied to the trigger button (e.g. width: '100%' with a full-width root) */
  triggerStyle?: React.CSSProperties
  /** Enable search input in dropdown for filtering options */
  searchable?: boolean
  /** Server-side search callback — when provided, search is delegated to the parent
   * and the Dropdown skips its own client-side filter. */
  onSearch?: (query: string) => void
  /** Called when the user scrolls near the bottom of the options list (infinite scroll). */
  onScrollEnd?: () => void
  /** Show a small spinner at the bottom of the list while more items are loading. */
  loadingMore?: boolean
  /** Optional class name applied to the trigger button */
  triggerClassName?: string
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  placeholder = 'Select...',
  onChange,
  disabled = false,
  className = '',
  /* required – accepted in props interface but unused in component */
  title,
  portal = false,
  rootStyle,
  triggerStyle,
  searchable = false,
  onSearch,
  onScrollEnd,
  loadingMore = false,
  triggerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  /** Prevents portaled menus from using `.dropdown__menu { top: 100% }` on `body` before fixed coords run. */
  const [portalMenuPositioned, setPortalMenuPositioned] = useState(false)
  const selectedOption = options.find((option) => option.value === value)
  // Portaled menus must not inherit layout utilities like `w-full` (viewport-wide
  // measure on first open). Keep typography/color utilities from className.
  const portalMenuClassName = useMemo(
    () =>
      className
        .split(/\s+/)
        .filter((token) => token && !/^(w-|min-w-|max-w-)/.test(token))
        .join(' '),
    [className],
  )

  // Filter options based on search (skipped when onSearch is provided — parent handles it)
  const filteredOptions = useMemo(() => {
    if (onSearch) return options // server-side search: show whatever parent gives us
    if (!searchable || !searchInput.trim()) return options
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchInput.toLowerCase())
    )
  }, [options, searchInput, searchable, onSearch])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isOpen, searchable])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, triggerRef.current)) return
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setIsOpen(false)
      setSearchInput('')
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  // Scroll handler for infinite scroll support
  const handleMenuScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollEnd) return
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      onScrollEnd()
    }
  }, [onScrollEnd])

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const triggerWidth = rect.width
    const offset = 6
    const viewportPadding = 8

    // Measure menu height for flip / maxHeight (width comes from the trigger).
    const menuRect = menuRef.current?.getBoundingClientRect()
    const measuredHeight = menuRect?.height ?? 200

    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
    const spaceAbove = rect.top - viewportPadding
    const openUp = measuredHeight + offset > spaceBelow && spaceAbove > spaceBelow

    const maxHeight = Math.max(
      140,
      Math.min(320, openUp ? spaceAbove - offset : spaceBelow - offset)
    )

    const top = openUp
      ? Math.max(viewportPadding, rect.top - Math.min(measuredHeight, maxHeight) - offset)
      : rect.bottom + offset

    // Align with trigger left by default, but shift if it would overflow the right edge.
    // Use triggerWidth (not measured menu width): the portaled menu may inherit
    // consumer classNames like `w-full`, which before inline width is applied makes
    // getBoundingClientRect() report ~viewport width and clamp left to ~8px (first open).
    const left = Math.max(
      viewportPadding,
      Math.min(rect.left, window.innerWidth - triggerWidth - viewportPadding)
    )

    // Below nav-rail (z-index 101) on normal pages; above modal overlays when nested in one.
    let resolvedZ = 90
    let el: HTMLElement | null = triggerRef.current?.parentElement ?? null
    while (el && el !== document.body) {
      const z = window.getComputedStyle(el).zIndex
      if (z && z !== 'auto') {
        const parsed = Number.parseInt(z, 10)
        if (!Number.isNaN(parsed) && parsed > resolvedZ) {
          resolvedZ = parsed
        }
      }
      el = el.parentElement
    }
    const portalZ = resolvedZ < 101 ? 90 : resolvedZ + 50

    setMenuStyle({
      position: 'fixed',
      top,
      left,
      width: triggerWidth, // Keep menu width same as trigger as per design
      maxHeight,
      right: 'auto',
      visibility: 'visible',
      pointerEvents: 'auto',
      zIndex: portalZ,
    })
  }, [])

  useLayoutEffect(() => {
    if (!portal || !isOpen) {
      setPortalMenuPositioned(false)
      return
    }
    updateMenuPosition()
    setPortalMenuPositioned(true)

    const handleReposition = () => updateMenuPosition()
    window.addEventListener('resize', handleReposition)

    const scrollRoots = new Set<EventTarget>([window])
    let el: HTMLElement | null = triggerRef.current?.parentElement ?? null
    while (el) {
      const st = window.getComputedStyle(el)
      if (/(auto|scroll)/.test(`${st.overflowY}${st.overflowX}${st.overflow}`)) {
        scrollRoots.add(el)
      }
      el = el.parentElement
    }
    scrollRoots.forEach((target) => {
      target.addEventListener('scroll', handleReposition, true)
    })

    return () => {
      window.removeEventListener('resize', handleReposition)
      scrollRoots.forEach((target) => {
        target.removeEventListener('scroll', handleReposition, true)
      })
      setPortalMenuPositioned(false)
    }
  }, [portal, isOpen, updateMenuPosition])

  // Close when the left nav rail expands so it is not obscured under the overlay.
  useEffect(() => {
    if (!portal || !isOpen) return
    const rail = document.querySelector('.nav-rail')
    if (!rail) return

    const syncWithNav = () => {
      if (rail.classList.contains('expanded')) {
        setIsOpen(false)
        setSearchInput('')
      }
    }

    syncWithNav()
    const observer = new MutationObserver(syncWithNav)
    observer.observe(rail, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [portal, isOpen])

  const portalMenuClassSuffix = portalMenuClassName ? ` ${portalMenuClassName}` : ''
  return (
    <div className={`dropdown ${className}`} ref={dropdownRef} style={rootStyle}>
      <button
        type="button"
        className={`dropdown__trigger ${isOpen ? 'dropdown__trigger--open' : ''} ${disabled ? 'dropdown__trigger--disabled' : ''} ${triggerClassName}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        title={selectedOption?.title ?? title}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedOption ? `Selected: ${selectedOption.label}` : `Select ${placeholder}`}
        ref={triggerRef}
        style={triggerStyle}
      >
        <span className={`dropdown__value-row ${selectedOption ? 'dropdown__value' : 'dropdown__placeholder'}`}>
          {selectedOption?.icon && (
            <span className="dropdown__option-icon dropdown__option-icon--trigger" aria-hidden>
              {selectedOption.icon}
            </span>
          )}
          <span className="dropdown__value-text">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`dropdown__arrow ${isOpen ? 'dropdown__arrow--rotated' : ''}`}
        />
      </button>

      {isOpen && (portal
        ? createPortal(
            <div
              className={`dropdown__menu dropdown__menu--portal${portalMenuPositioned ? ' dropdown__menu--positioned' : ''}${portalMenuClassSuffix}`}
              role="listbox"
              style={menuStyle}
              ref={menuRef}
              onScroll={handleMenuScroll}
            >
              {searchable && (
                <div className="dropdown__search-wrapper">
                  <Search size={16} className="dropdown__search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="dropdown__search-input"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value)
                      onSearch?.(e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`dropdown__option ${option.value === value ? 'dropdown__option--selected' : ''} ${option.disabled ? 'dropdown__option--disabled' : ''}`}
                    onClick={() => !option.disabled && handleSelect(option.value)}
                    disabled={option.disabled}
                    role="option"
                    aria-selected={option.value === value}
                    title={option.title}
                  >
                    {option.icon && (
                      <span className="dropdown__option-icon" aria-hidden>
                        {option.icon}
                      </span>
                    )}
                    <span className="dropdown__option-label">{option.label}</span>
                    {option.value === value && <Check size={14} className="dropdown__option-check" />}
                  </button>
                ))
              ) : (
                <div className="dropdown__no-results">No results found</div>
              )}
              {loadingMore && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0', color: 'var(--text-muted, #94a3b8)', fontSize: 11 }}>
                  Loading…
                </div>
              )}
            </div>,
            document.body
          )
        : (
          <div className="dropdown__menu" role="listbox" ref={menuRef} onScroll={handleMenuScroll}>
            {searchable && (
              <div className="dropdown__search-wrapper">
                <Search size={16} className="dropdown__search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="dropdown__search-input"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value)
                    onSearch?.(e.target.value)
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`dropdown__option ${option.value === value ? 'dropdown__option--selected' : ''} ${option.disabled ? 'dropdown__option--disabled' : ''}`}
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  disabled={option.disabled}
                  role="option"
                  aria-selected={option.value === value}
                  title={option.title}
                >
                  {option.icon && (
                    <span className="dropdown__option-icon" aria-hidden>
                      {option.icon}
                    </span>
                  )}
                  <span className="dropdown__option-label">{option.label}</span>
                  {option.value === value && <Check size={14} className="dropdown__option-check" />}
                </button>
              ))
            ) : (
              <div className="dropdown__no-results">No results found</div>
            )}
            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0', color: 'var(--text-muted, #94a3b8)', fontSize: 11 }}>
                Loading…
              </div>
            )}
          </div>
        )
      )}
    </div>
  )
}

export default Dropdown
